import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCIES, type Currency } from '@atek/estimate-pricing';
import { Input } from '@/components/ui/input';
import { FormField } from '@/shared/components/form';
import { useCompanyContacts } from '@/features/crm/hooks/useCrmCompanyDetail';
import type { Presenter } from '../../types';
import { NativeSelect } from '../NativeSelect';
import { EditorSection } from './EditorSection';
import { NumberField } from './NumberField';
import type { SectionProps } from './types';

interface Props extends SectionProps {
  companyId: string | null;
  companyName: string | null;
  /** The company logo field (crm feature); null when the estimate has no company. */
  logo: ReactNode;
}

export function ProposalSection({ draft, dispatch, readOnly, errors, companyId, companyName, logo }: Props) {
  const { t } = useTranslation('crm');
  const { data: contacts } = useCompanyContacts(companyId ?? '');
  const setField = (field: 'title' | 'language' | 'validUntil' | 'coverContactId', value: string | null) =>
    dispatch({ type: 'setField', field, value });
  const presenterField = (field: keyof Presenter, label: string) => (
    <FormField label={label} htmlFor={`est-presenter-${field}`} error={errors[`presenter.${field}`]}>
      <Input
        id={`est-presenter-${field}`}
        value={draft.presenter[field]}
        maxLength={120}
        disabled={readOnly}
        onChange={(e) => dispatch({ type: 'setPresenter', field, value: e.target.value })}
      />
    </FormField>
  );

  return (
    <EditorSection title={t('estimates.proposal.title')}>
      <div className="flex items-start gap-4">
        {logo}
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{t('estimates.proposal.customer')}</p>
          <p className="font-medium">{companyName ?? '—'}</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t('estimates.proposal.titleField')} htmlFor="est-title" required error={errors.title} className="sm:col-span-2">
          <Input id="est-title" value={draft.title} maxLength={200} disabled={readOnly} onChange={(e) => setField('title', e.target.value)} />
        </FormField>
        <FormField label={t('estimates.proposal.language')} htmlFor="est-language" hint={t('estimates.proposal.languageHint')} error={errors.language}>
          <NativeSelect id="est-language" value={draft.language} disabled={readOnly} onChange={(e) => setField('language', e.target.value)}>
            <option value="en">{t('estimates.proposal.languages.en')}</option>
            <option value="fr">{t('estimates.proposal.languages.fr')}</option>
          </NativeSelect>
        </FormField>
        <FormField label={t('estimates.proposal.validUntil')} htmlFor="est-valid-until" error={errors.validUntil}>
          <Input id="est-valid-until" type="date" value={draft.validUntil} disabled={readOnly} onChange={(e) => setField('validUntil', e.target.value)} />
        </FormField>
        <FormField label={t('estimates.proposal.currency')} htmlFor="est-currency" error={errors['inputs.currency']}>
          <NativeSelect
            id="est-currency"
            value={draft.inputs.currency}
            disabled={readOnly}
            onChange={(e) => dispatch({ type: 'setInputs', patch: { currency: e.target.value as Currency } })}
          >
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </NativeSelect>
        </FormField>
        {draft.inputs.currency !== 'CAD' && (
          <NumberField
            id="est-fx-rate"
            label={t('estimates.proposal.fxRate', { currency: draft.inputs.currency })}
            hint={t('estimates.proposal.fxRateHint')}
            value={draft.inputs.fxRate}
            step={0.0001}
            disabled={readOnly}
            error={errors['inputs.fxRate']}
            onChange={(fxRate) => dispatch({ type: 'setInputs', patch: { fxRate: fxRate ?? 0 } })}
          />
        )}
        <FormField label={t('estimates.proposal.coverContact')} htmlFor="est-cover-contact" className="sm:col-span-2" error={errors.coverContactId}>
          <NativeSelect
            id="est-cover-contact"
            value={draft.coverContactId ?? ''}
            disabled={readOnly || !companyId}
            onChange={(e) => setField('coverContactId', e.target.value || null)}
          >
            <option value="">{t('estimates.proposal.noCoverContact')}</option>
            {(contacts?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>{[`${c.first_name} ${c.last_name}`.trim(), c.title].filter(Boolean).join(' – ')}</option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      <div>
        <p className="mb-2 text-sm font-medium">{t('estimates.proposal.presenter')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {presenterField('name', t('estimates.proposal.presenterName'))}
          {presenterField('title', t('estimates.proposal.presenterTitle'))}
          {presenterField('phone', t('estimates.proposal.presenterPhone'))}
          {presenterField('email', t('estimates.proposal.presenterEmail'))}
        </div>
      </div>
    </EditorSection>
  );
}
